import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  announcements as annApi,
  ApiError,
  dens as densApi,
  units as unitsApi,
  type AnnouncementPost,
  type DenRow,
  type PostInput,
  type PostVisibility,
} from '../api';
import { useAuth } from '../auth';
import { useUnits } from '../units';

export function Announcements() {
  const { unitId = '' } = useParams();
  const { me } = useAuth();
  const { currentUnit, hasRole, reload } = useUnits();
  const canPost = hasRole('ADMIN') || hasRole('DEN_LEADER');
  const isAdmin = hasRole('ADMIN');

  const [posts, setPosts] = useState<AnnouncementPost[] | null>(null);
  const [dens, setDens] = useState<DenRow[]>([]);
  const [denId, setDenId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    annApi
      .list(unitId, { denId: denId || undefined, from: from || undefined, to: to || undefined })
      .then(setPosts)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load announcements'));
  }, [unitId, denId, from, to]);

  useEffect(load, [load]);
  useEffect(() => {
    if (canPost) densApi.list(unitId).then((r) => setDens(r.dens)).catch(() => setDens([]));
  }, [unitId, canPost]);

  if (!currentUnit) return <Navigate to="/" replace />;

  return (
    <div className="panel">
      <h1>Announcements</h1>
      <p className="muted">{currentUnit.name} · archive</p>
      {error && <p className="error">{error}</p>}

      {isAdmin && <PublicArchiveSettings unit={currentUnit} onChanged={reload} />}

      {canPost && <PostForm unitId={unitId} dens={dens} isAdmin={isAdmin} onSaved={load} />}

      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }}>
        <label className="filter">
          Den{' '}
          <select value={denId} onChange={(e) => setDenId(e.target.value)}>
            <option value="">All</option>
            {dens.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="filter">
          To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        {(denId || from || to) && (
          <button
            onClick={() => {
              setDenId('');
              setFrom('');
              setTo('');
            }}
          >
            Clear
          </button>
        )}
      </div>

      {!posts ? (
        <p className="muted">Loading…</p>
      ) : posts.length === 0 ? (
        <p className="muted">No announcements.</p>
      ) : (
        posts.map((p) => (
          <PostCard
            key={p.id}
            unitId={unitId}
            post={p}
            dens={dens}
            isAdmin={isAdmin}
            isAuthor={p.authorId === me?.user.id}
            onChanged={load}
          />
        ))
      )}
    </div>
  );
}

function PublicArchiveSettings({
  unit,
  onChanged,
}: {
  unit: { id: string; name: string; publicArchiveEnabled: boolean; defaultPostVisibility: string };
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const publicUrl = `${window.location.origin}/u/${unit.id}`;

  async function patch(p: { publicArchiveEnabled?: boolean; defaultPostVisibility?: 'public' | 'members_only' }) {
    setBusy(true);
    try {
      await unitsApi.updateSettings(unit.id, p);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="callout" style={{ marginBottom: 12 }}>
      <label className="filter">
        <input
          type="checkbox"
          checked={unit.publicArchiveEnabled}
          disabled={busy}
          onChange={(e) => patch({ publicArchiveEnabled: e.target.checked })}
        />{' '}
        Public archive page
      </label>
      {unit.publicArchiveEnabled && (
        <p className="muted small">
          Live at{' '}
          <a href={publicUrl} target="_blank" rel="noreferrer">
            {publicUrl}
          </a>{' '}
          — shows only posts marked <strong>public</strong>.
        </p>
      )}
      <label className="filter" style={{ marginTop: 6 }}>
        New posts default to{' '}
        <select
          value={unit.defaultPostVisibility}
          disabled={busy}
          onChange={(e) => patch({ defaultPostVisibility: e.target.value as 'public' | 'members_only' })}
        >
          <option value="members_only">Members only</option>
          <option value="public">Public</option>
        </select>
      </label>
    </div>
  );
}

function PostCard({
  unitId,
  post: p,
  dens,
  isAdmin,
  isAuthor,
  onChanged,
}: {
  unitId: string;
  post: AnnouncementPost;
  dens: DenRow[];
  isAdmin: boolean;
  isAuthor: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const canModify = isAdmin || isAuthor;

  if (editing) {
    return (
      <div className="req-block">
        <PostForm
          unitId={unitId}
          dens={dens}
          isAdmin={isAdmin}
          existing={p}
          onSaved={() => {
            setEditing(false);
            onChanged();
          }}
          onCancel={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="req-block">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong>{p.title}</strong>
        <span className="muted small">
          {new Date(p.publishedAt).toLocaleDateString()}
          {p.editedAt ? ' · edited' : ''}
        </span>
      </div>
      <p className="muted small">
        {p.authorName}
        {p.denName && <span className="badge" style={{ marginLeft: 6 }}>{p.denName}</span>}
        <span className="badge" style={{ marginLeft: 6 }}>
          {p.visibility === 'public' ? 'public' : 'members only'}
        </span>
      </p>
      <p style={{ whiteSpace: 'pre-wrap' }}>{p.body}</p>
      {canModify && (
        <div className="row" style={{ gap: 8 }}>
          <button className="linklike" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            className="linklike"
            onClick={async () => {
              if (confirm(`Delete "${p.title}" from the archive?`)) {
                await annApi.remove(unitId, p.id);
                onChanged();
              }
            }}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

function PostForm({
  unitId,
  dens,
  isAdmin,
  existing,
  onSaved,
  onCancel,
}: {
  unitId: string;
  dens: DenRow[];
  isAdmin: boolean;
  existing?: AnnouncementPost;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [body, setBody] = useState(existing?.body ?? '');
  const [denId, setDenId] = useState(existing?.denId ?? '');
  const [visibility, setVisibility] = useState<PostVisibility>(existing?.visibility ?? 'members_only');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const payload: PostInput = { title, body, denId: denId || null, visibility };
      if (existing) await annApi.update(unitId, existing.id, payload);
      else await annApi.create(unitId, payload);
      if (!existing) {
        setTitle('');
        setBody('');
        setDenId('');
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the post');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="event-form" onSubmit={submit}>
      <input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} required />
      <textarea
        rows={4}
        placeholder="Write the announcement…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
      />
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <select value={denId} onChange={(e) => setDenId(e.target.value)}>
          <option value="">Whole pack</option>
          {dens.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </select>
        <select value={visibility} onChange={(e) => setVisibility(e.target.value as PostVisibility)}>
          <option value="members_only">Members only</option>
          <option value="public">Public</option>
        </select>
        {visibility === 'public' && !isAdmin && (
          <span className="muted small">Public posts show on the Unit's public page if enabled.</span>
        )}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button type="submit" className="primary" disabled={busy || !title || !body}>
          {existing ? 'Save post' : 'Post announcement'}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
        {error && <span className="error">{error}</span>}
      </div>
    </form>
  );
}
